import { Switch } from "@clockwise/design-system";
import { WebappSurface } from "@clockwise/schema";
import { RecurrenceType } from "@clockwise/schema/v2";
import EventCard from "@clockwise/web-commons/src/components/EventCard";
import EventCardColumns from "@clockwise/web-commons/src/components/EventCardColumns";
import EventCardColumn from "@clockwise/web-commons/src/components/EventCardColumns/EventCardColumn";
import { useGatewayQuery } from "@clockwise/web-commons/src/network/apollo/gateway-provider";
import { Tooltip } from "@material-ui/core";
import { every, filter } from "lodash";
import { Duration, Interval } from "luxon";
import React from "react";
import { useMonetization } from "../../hooks/useMonetization";
import { removeAutopilotEmoji } from "../../util/event.util";
import { useSetFlexibility } from "../flexible-meetings/useSetFlexibility";
import { ScrollIntoView } from "../ui-focus-wrappers/ScrollIntoView";
import OneOnOneGuidance from "./OneOnOneGuidance";
import { OneOnOnesDashboardDocument } from "./graphql/__generated__/OneOneOnesDashboard.v2.generated";

export const OneOnOnes = () => {
  const { error, loading, data } = useGatewayQuery(OneOnOnesDashboardDocument);
  const { handleFlexibilityChange, handleAllFlexibilityChange } = useSetFlexibility(
    data?.flexibleMeetingsDashboard.oneOnOnes ?? [],
    "One-on-ones control",
  );
  const { canUserAccessFlexMeetings } = useMonetization();

  const oneOnOnes = (data?.flexibleMeetingsDashboard.oneOnOnes ?? []).map(
    ({
      id,
      externalEventId,
      dateOrTimeRange,
      flexibility,
      canMarkFlexible,
      recurrence,
      timeDescription,
      title,
    }) => ({
      internalId: id,
      id: externalEventId,
      duration: Interval.fromISO(
        dateOrTimeRange.__typename === "DateRange"
          ? dateOrTimeRange.dateRange
          : dateOrTimeRange.timeRange,
      ).toDuration(),
      flexible: !!flexibility?.isFlexible,
      editable: canMarkFlexible,
      title: removeAutopilotEmoji(title),
      recurrence: recurrence?.type,
      recurrenceText: timeDescription,
    }),
  );

  const oneOnOnesAverage = Duration.fromObject({
    minutes: data?.flexibleMeetingsDashboard.oneOnOneAverage ?? 0,
  });

  const weeklyOneOnOnes = filter(oneOnOnes, { recurrence: RecurrenceType.Weekly });
  const biweeklyOneOnOnes = filter(oneOnOnes, {
    recurrence: RecurrenceType.Fortnightly,
  });
  const infrequentOneOnOnes = filter(
    oneOnOnes,
    ({ recurrence }) =>
      recurrence !== RecurrenceType.Weekly && recurrence !== RecurrenceType.Fortnightly,
  );

  const editableOneOnOnes = filter(oneOnOnes, "editable");
  const allFlexible = every(editableOneOnOnes, "flexible");
  const fetchError = error?.message ? "Unable to fetch One-on-ones" : undefined;

  const switchTooltipText = canUserAccessFlexMeetings ? "" : "Upgrade to mark meetings as flexible";

  return (
    <ScrollIntoView focusSection={WebappSurface.FlexibleMeetings}>
      <div className="cw-mb-3 cw-border-b cw-border-default cw-border-solid">
        <div className="cw-flex cw-flex-wrap cw-items-center cw-mb-1">
          <h2 className="cw-heading-2xl cw-grow cw-mb-0">One-on-ones</h2>
          <div className="cw-relative">
            <Tooltip title={switchTooltipText}>
              <div className="cw-pb-[2px]">
                <Switch
                  label={<div className="cw-body-sm">Mark all one-on-ones flexible</div>}
                  labelPlacement="start"
                  checked={allFlexible}
                  disabled={loading || !oneOnOnes.length || !canUserAccessFlexMeetings}
                  onChange={handleAllFlexibilityChange}
                  cw-id="mark-all-flexible-switch"
                />
              </div>
            </Tooltip>
          </div>
        </div>
      </div>
      <OneOnOneGuidance loading={loading} weeklyAverage={oneOnOnesAverage} />
      <EventCardColumns
        headingLevel="h3"
        loading={loading}
        errorMessage={fetchError}
        onFlexibilityChange={handleFlexibilityChange}
      >
        <EventCardColumn heading="Weekly" emptyMessage="No weekly one-on-ones">
          {weeklyOneOnOnes.map(({ id, editable, flexible, recurrenceText, title }) => (
            <EventCard
              editable={editable}
              eventId={id}
              flexible={flexible && canUserAccessFlexMeetings}
              key={id}
              recurrence={recurrenceText}
              title={title}
              restricted={!canUserAccessFlexMeetings}
            />
          ))}
        </EventCardColumn>
        <EventCardColumn
          heading="Every 2 weeks"
          emptyMessage="No one-on-ones that repeat every 2 weeks"
        >
          {biweeklyOneOnOnes.map(({ id, editable, flexible, recurrenceText, title }) => (
            <EventCard
              editable={editable}
              eventId={id}
              flexible={flexible && canUserAccessFlexMeetings}
              key={id}
              recurrence={recurrenceText}
              title={title}
              restricted={!canUserAccessFlexMeetings}
            />
          ))}
        </EventCardColumn>
        <EventCardColumn heading="Less frequent" emptyMessage="No less frequent one-on-ones">
          {infrequentOneOnOnes.map(({ id, editable, flexible, recurrenceText, title }) => (
            <EventCard
              editable={editable}
              eventId={id}
              flexible={flexible && canUserAccessFlexMeetings}
              key={id}
              recurrence={recurrenceText}
              title={title}
              restricted={!canUserAccessFlexMeetings}
            />
          ))}
        </EventCardColumn>
      </EventCardColumns>
    </ScrollIntoView>
  );
};
